import { Link } from "react-router-dom";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { SIGNUP_FORM_URL } from "../external-links";
import { type MarketplaceGuide, marketplaceGuides } from "../data/marketplace-guides";
import { Phone } from "../design/components/mobile/phone-shell";
import { TodayScreen } from "../design/sections/mobile-screens/today-screen";
import { InsightScreen } from "../design/sections/mobile-screens/insight-screen";
import { SkillsScreen } from "../design/sections/mobile-screens/skills-screen";
import { VaultScreen } from "../design/sections/mobile-screens/vault-screen";

// ─── Data ────────────────────────────────────────────────────────────────────

const howSteps = [
  {
    num: "01",
    title: "Add your health data",
    text: "Upload lab reports, connect wearables, or snap a photo. Kin structures everything into your private vault.",
    detail: "Labs · Wearables · CGM · Notes · Genetics",
  },
  {
    num: "02",
    title: "Install a skill built by experts",
    text: "Browse skills created by researchers and practitioners. Each one runs a specific analysis protocol on your data.",
    detail: "Longevity · Sleep · Hormones · Cardio · Metabolic",
  },
  {
    num: "03",
    title: "Get results you can act on",
    text: "Personalized insights, trend tracking, flagged biomarkers, and doctor-ready summaries — in minutes, not weeks.",
    detail: "Dashboards · Timelines · Action plans · QR sharing",
  },
];

const privacyPoints = [
  {
    icon: "shield",
    title: "Your vault, your rules",
    text: "End-to-end encrypted. Kin cannot read your health data. You control every permission.",
  },
  {
    icon: "lock",
    title: "Skills run in isolation",
    text: "Each skill processes data in a sandboxed environment with no internet access. Your data stays in your vault.",
  },
  {
    icon: "eye",
    title: "Transparent by design",
    text: "Every insight shows what data it used and why. Nothing is a black box.",
  },
  {
    icon: "user",
    title: "Human decisions, always",
    text: "Kin helps you understand your data. It never replaces clinical judgment or makes medical claims.",
  },
];

const featured = marketplaceGuides.filter((g) => g.featured).slice(0, 3);
const featuredSkills = featured.length > 0 ? featured : marketplaceGuides.slice(0, 3);

// ─── Sub-components ───────────────────────────────────────────────────────────

function PrivacyIcon({ icon }: { icon: string }) {
  switch (icon) {
    case "shield":
      return (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
          <path
            d="M12 4.5 18 7v5c0 4.25-2.5 6.9-6 8-3.5-1.1-6-3.75-6-8V7l6-2.5Z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "lock":
      return (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
          <rect
            x="6"
            y="10"
            width="12"
            height="10"
            rx="2"
            stroke="currentColor"
            strokeWidth="1.7"
          />
          <path
            d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </svg>
      );
    case "eye":
      return (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
          <path
            d="M2.5 12S6 6.5 12 6.5 21.5 12 21.5 12 18 17.5 12 17.5 2.5 12 2.5 12Z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
          <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.7" />
          <path
            d="M5.5 20c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </svg>
      );
  }
}

function CategoryIcon({ category }: { category: string }) {
  switch (category) {
    case "Longevity":
      return (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
          <path
            d="M4 12c1.7-3 3.2-4.5 5-4.5 3 0 4 9 7 9 1.8 0 3.3-1.5 4.9-4.5-1.6-3-3.1-4.5-4.9-4.5-3 0-4 9-7 9-1.8 0-3.3-1.5-5-4.5Z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "Metabolic Health":
      return (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
          <path
            d="M12 4.5c3.5 3.8 5.25 6.3 5.25 8.75a5.25 5.25 0 1 1-10.5 0c0-2.45 1.75-4.95 5.25-8.75Z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "Sleep and Recovery":
    case "Sleep & Recovery":
      return (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
          <path
            d="M16.5 5.25a7 7 0 1 0 2.25 13.65A7.75 7.75 0 0 1 16.5 5.25Z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1.7" />
          <path d="M12 8v4l2.5 2.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      );
  }
}

function categoryTone(category: string) {
  if (category === "Longevity") return "text-kin-green-dark";
  if (category === "Metabolic Health") return "text-kin-orange";
  if (category === "Sleep and Recovery" || category === "Sleep & Recovery")
    return "text-kin-lavender";
  return "text-kin-olive";
}

function MiniSparkline({ data }: { data: number[] }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const h = 28;
  const w = 64;
  const points = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-7 w-16" aria-hidden="true">
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.6"
      />
    </svg>
  );
}

function SkillCard({ guide }: { guide: MarketplaceGuide }) {
  const spark = guide.previewCards[0]?.spark;
  return (
    <article className="kin-surface group relative overflow-hidden rounded-2xl p-5 transition hover:border-kin-olive/20">
      <div className="flex items-start justify-between">
        <span
          className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-kin-olive/12 bg-kin-white ${categoryTone(guide.category)}`}
        >
          <CategoryIcon category={guide.category} />
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-kin-muted">
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
            <path d="m12 3.75 2.43 4.92 5.43.79-3.93 3.84.93 5.42L12 16.16l-4.86 2.56.93-5.42-3.93-3.84 5.43-.79L12 3.75Z" />
          </svg>
          {guide.rating}
        </span>
      </div>

      <h3 className="mt-3 text-lg font-semibold text-kin-black">{guide.name}</h3>
      <p className="mt-0.5 text-xs text-kin-muted">{guide.creator}</p>
      <p className="mt-2 text-sm leading-relaxed text-kin-body/88">{guide.description}</p>

      {guide.previewCards[0] && (
        <div className="mt-4 flex items-center justify-between rounded-xl border border-kin-olive/10 bg-kin-white/80 px-3 py-2.5">
          <div>
            <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-kin-muted">
              {guide.previewCards[0].label}
            </p>
            <p className="mt-0.5 text-base font-semibold text-kin-black">
              {guide.previewCards[0].value}
            </p>
          </div>
          {spark && (
            <span className={categoryTone(guide.category)}>
              <MiniSparkline data={spark} />
            </span>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-kin-muted">{guide.installs} installs</span>
        <Link to={`/marketplace/${guide.slug}`}>
          <Button size="small">View Skill</Button>
        </Link>
      </div>
    </article>
  );
}

const previewNav = [
  { label: "Home", active: true },
  { label: "Vault", count: "24" },
  { label: "Biomarkers", count: "18" },
  { label: "Skills", count: "7" },
];

const previewFeed = [
  {
    title: "Sleep report added",
    time: "Today",
    chips: ["Oura", "Recovery"],
    status: "3 skills reacted",
  },
  {
    title: "Blood panel parsed",
    time: "09:42",
    chips: ["LDL-C", "ApoB", "hs-CRP"],
    status: "Ready",
  },
  {
    title: "Meal photo processing",
    time: "Processing",
    chips: ["Dinner", "Protein"],
    status: "Skills will react",
  },
];

function WorkspacePreview() {
  return (
    <div className="relative mx-auto w-full max-w-[780px] lg:max-w-none">
      <div className="overflow-hidden rounded-[28px] border border-kin-border-strong bg-kin-page shadow-[0_24px_64px_rgba(28,28,30,0.10),0_4px_16px_rgba(28,28,30,0.05)]">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center border-b border-kin-border bg-gradient-to-b from-kin-panel to-kin-active px-4 py-3">
          <div className="flex gap-2">
            <span className="h-3 w-3 rounded-full bg-kin-rose-canonical" />
            <span className="h-3 w-3 rounded-full bg-kin-canonical-gold" />
            <span className="h-3 w-3 rounded-full bg-kin-primary" />
          </div>
          <div className="hidden min-w-[260px] rounded-full border border-kin-border bg-kin-card px-5 py-1.5 text-center text-[11.5px] font-medium text-kin-muted-canonical sm:block">
            kin.health / home
          </div>
        </div>

        <div className="grid min-h-[520px] grid-cols-[64px_minmax(0,1fr)] bg-kin-page sm:grid-cols-[204px_minmax(0,1fr)] lg:min-h-[560px]">
          <PreviewSidebar />

          <div className="min-w-0">
            <div className="flex items-center gap-2 border-b border-kin-border bg-kin-page/75 px-4 py-3 backdrop-blur sm:px-7">
              <span className="text-[12px] font-medium text-kin-muted-canonical">Home</span>
              <span className="text-kin-border-strong">/</span>
              <span className="text-[12px] font-semibold text-kin-ink">Today</span>
            </div>

            <div className="px-4 py-5 sm:px-7 sm:py-7">
              <div className="text-[10.5px] font-semibold tracking-[0.12em] text-kin-muted-canonical uppercase">
                Sun, April 13
              </div>
              <h2 className="mt-1 font-serif text-[34px] leading-[1.05] font-medium tracking-[-0.015em] text-kin-ink sm:text-[44px]">
                Good morning, Sverre
              </h2>

              <div className="mt-5 grid gap-4 xl:grid-cols-[minmax(0,1fr)_260px]">
                <PreviewSkillCard />
                <PreviewFeed />
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <PreviewSummaryTile
                  kicker="Health data"
                  title="Biomarkers"
                  value="18"
                  label="Tracked markers"
                  tone="green"
                />
                <PreviewSummaryTile
                  kicker="Vault"
                  title="Last upload"
                  value="Today"
                  label="Encrypted and indexed"
                  tone="gold"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewSidebar() {
  return (
    <aside className="flex min-w-0 flex-col border-r border-kin-border bg-kin-panel px-2 py-4 sm:px-3">
      <div className="mb-4 flex items-center justify-center gap-2 px-1 sm:justify-start sm:px-2">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-kin-primary text-sm font-semibold text-kin-card">
          K
        </span>
        <span className="hidden font-serif text-[22px] font-semibold text-kin-ink sm:inline">
          Kin
        </span>
      </div>

      <div className="mb-3 hidden items-center gap-2 rounded-[10px] border border-kin-border bg-kin-card px-3 py-2 text-[12px] text-kin-muted-canonical sm:flex">
        <SearchIcon />
        <span className="min-w-0 flex-1 truncate">Search vault</span>
        <kbd className="rounded border border-kin-border-strong px-1.5 text-[10px] text-kin-muted-canonical">
          K
        </kbd>
      </div>

      <nav className="space-y-1">
        {previewNav.map((item) => (
          <div
            key={item.label}
            className={`flex items-center gap-2 rounded-lg px-2 py-2 text-[12.5px] font-medium ${
              item.active
                ? "bg-kin-card text-kin-ink shadow-[0_1px_2px_rgba(28,28,30,0.04)]"
                : "text-kin-body-canonical"
            }`}
          >
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-kin-primary" />
            <span className="hidden min-w-0 flex-1 truncate sm:inline">{item.label}</span>
            {item.count && (
              <span className="hidden text-[10.5px] text-kin-muted-canonical sm:inline">
                {item.count}
              </span>
            )}
          </div>
        ))}
      </nav>

      <div className="mt-auto hidden rounded-xl border border-kin-border bg-kin-card p-3 sm:block">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-kin-primary" />
          <span className="text-[10px] font-bold tracking-[0.08em] text-kin-primary-strong uppercase">
            Vault encrypted
          </span>
        </div>
        <p className="mt-2 text-[11.5px] leading-[1.45] text-kin-muted-canonical">
          24 files indexed. Access remains revocable per skill.
        </p>
      </div>
    </aside>
  );
}

function PreviewSkillCard() {
  return (
    <article className="overflow-hidden rounded-[22px] border border-kin-border bg-kin-card shadow-[0_8px_24px_rgba(28,28,30,0.04)]">
      <div className="grid h-1 grid-cols-3">
        <span className="bg-kin-blue-canonical" />
        <span className="bg-kin-primary" />
        <span className="bg-kin-canonical-gold" />
      </div>
      <div className="p-5 sm:p-6">
        <div className="text-[10.5px] font-semibold tracking-[0.12em] text-kin-muted-canonical uppercase">
          Most relevant today
        </div>
        <div className="mt-3 flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-kin-lilac-soft font-serif text-[20px] font-semibold text-kin-ink">
            S
          </span>
          <div className="min-w-0">
            <h3 className="truncate font-serif text-[26px] leading-[1.1] font-medium tracking-[-0.01em] text-kin-ink sm:text-[30px]">
              Sleep Deep Dive
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-kin-muted-canonical">
              <span className="font-semibold text-kin-lilac">Sara Chen</span>
              <span>Updated today</span>
            </div>
          </div>
        </div>

        <p className="mt-4 max-w-[560px] text-[13.5px] leading-[1.6] text-kin-body-canonical">
          Recovery is improving, but late meals overlap with lighter sleep. Nutrition timing may be
          contributing to the pattern.
        </p>

        <div className="mt-5 grid gap-2 sm:grid-cols-3">
          <PreviewMetric kicker="Recovery" value="82" tone="green" />
          <PreviewMetric kicker="Sleep debt" value="1.4h" tone="gold" />
          <div className="rounded-xl bg-kin-lilac-soft p-3">
            <div className="text-[10.5px] font-semibold tracking-[0.1em] text-kin-lilac uppercase">
              Across skills
            </div>
            <p className="mt-2 text-[11.5px] leading-[1.4] text-kin-body-canonical">
              Meal logs overlap with best recovery nights.
            </p>
          </div>
        </div>

        <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-kin-active px-3 py-2 text-[11.5px] text-kin-muted-canonical">
          <span className="h-1.5 w-1.5 rounded-full bg-kin-primary" />3 skills reacted to this
          update
        </div>
      </div>
    </article>
  );
}

function PreviewMetric({
  kicker,
  value,
  tone,
}: {
  kicker: string;
  value: string;
  tone: "green" | "gold";
}) {
  return (
    <div
      className={
        tone === "green"
          ? "rounded-xl bg-kin-primary/12 p-3"
          : "rounded-xl bg-kin-canonical-gold-soft p-3"
      }
    >
      <div
        className={`text-[10.5px] font-semibold tracking-[0.1em] uppercase ${
          tone === "green" ? "text-kin-primary-strong" : "text-kin-body-canonical"
        }`}
      >
        {kicker}
      </div>
      <div className="mt-2 font-serif text-[26px] leading-none font-medium text-kin-ink">
        {value}
      </div>
    </div>
  );
}

function PreviewFeed() {
  return (
    <aside className="hidden xl:block">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[10.5px] font-semibold tracking-[0.12em] text-kin-muted-canonical uppercase">
          Feed
        </span>
        <span className="text-[12px] font-semibold text-kin-primary-strong">See all</span>
      </div>
      <div className="space-y-3">
        {previewFeed.map((item) => (
          <div
            key={item.title}
            className="rounded-[14px] border border-kin-border bg-kin-card p-3.5"
          >
            <div className="flex items-baseline justify-between gap-3">
              <h4 className="text-[13.5px] font-semibold text-kin-ink">{item.title}</h4>
              <span
                className={`text-[11px] ${
                  item.time === "Processing"
                    ? "font-semibold text-kin-tangerine"
                    : "text-kin-muted-canonical"
                }`}
              >
                {item.time}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {item.chips.map((chip) => (
                <span
                  key={chip}
                  className="rounded-full bg-kin-active px-2 py-1 text-[10.5px] font-medium text-kin-body-canonical"
                >
                  {chip}
                </span>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 text-[11.5px] font-semibold text-kin-primary-strong">
              <span className="h-1.5 w-1.5 rounded-full bg-kin-primary" />
              {item.status}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}

function PreviewSummaryTile({
  kicker,
  title,
  value,
  label,
  tone,
}: {
  kicker: string;
  title: string;
  value: string;
  label: string;
  tone: "green" | "gold";
}) {
  return (
    <div className="rounded-[16px] border border-kin-border bg-kin-card px-4 py-3.5">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-[10.5px] font-semibold tracking-[0.12em] text-kin-muted-canonical uppercase">
            {kicker}
          </div>
          <h3 className="mt-0.5 font-serif text-[22px] leading-[1.1] font-medium text-kin-ink">
            {title}
          </h3>
        </div>
        <span
          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
            tone === "green"
              ? "bg-kin-primary/12 text-kin-primary-strong"
              : "bg-kin-canonical-gold-soft text-kin-body-canonical"
          }`}
        >
          {value}
        </span>
      </div>
      <p className="mt-2 text-[12px] text-kin-muted-canonical">{label}</p>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.7" />
      <path d="m16 16 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function LandingPage() {
  return (
    <div className="bg-kin-bg px-6 pb-16 pt-6 sm:px-8 sm:pb-20 sm:pt-8">
      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section className="mx-auto grid min-h-[calc(100svh-96px)] max-w-7xl gap-8 py-8 lg:grid-cols-[0.72fr_1.28fr] lg:items-center lg:py-10">
        <div className="relative z-10">
          <span className="inline-flex rounded-full border border-kin-border bg-kin-card px-3 py-1 text-[11px] font-semibold tracking-[0.08em] text-kin-primary-strong uppercase">
            Private beta - limited spots
          </span>

          <h1 className="mt-5 max-w-[660px] font-serif text-[48px] leading-[0.98] font-medium tracking-[-0.015em] text-kin-ink sm:text-[64px] lg:text-[76px]">
            Your health data, finally working for you.
          </h1>

          <p className="mt-5 max-w-xl text-[17px] leading-[1.65] text-kin-body-canonical sm:text-[18px]">
            Upload your labs and wearable data. Install an expert AI skill. Get personalized
            insights in minutes — all inside your own encrypted vault.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/marketplace">
              <Button
                size="large"
                className="border-kin-primary-action/20 bg-kin-primary text-kin-card hover:bg-kin-primary-strong"
              >
                Browse Skills
              </Button>
            </Link>
            <a href={SIGNUP_FORM_URL} target="_blank" rel="noopener noreferrer">
              <Button
                variant="secondary"
                size="large"
                className="border-kin-border-strong bg-kin-card text-kin-ink hover:bg-kin-active"
              >
                Get Early Access
              </Button>
            </a>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <span className="rounded-full bg-kin-primary/12 px-3 py-1.5 text-[12px] font-medium text-kin-primary-action">
              Encrypted vault
            </span>
            <span className="rounded-full bg-kin-lilac-soft px-3 py-1.5 text-[12px] font-medium text-kin-ink">
              Expert AI skills
            </span>
            <span className="rounded-full bg-kin-canonical-gold-soft px-3 py-1.5 text-[12px] font-medium text-kin-ink">
              Doctor-ready sharing
            </span>
          </div>
        </div>

        <WorkspacePreview />
      </section>

      {/* ── Featured Skills ─────────────────────────────────────────────────── */}
      <section className="mx-auto mt-20 max-w-6xl">
        <div className="text-center">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
            Popular Skills
          </p>
          <h2 className="mt-3 font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl">
            Expert protocols, ready to install.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-kin-body/90">
            Health researchers, doctors, and practitioners package their methodologies as AI skills.
            Pick one, run it on your data, and see results in minutes.
          </p>
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-3">
          {featuredSkills.map((guide) => (
            <SkillCard key={guide.slug} guide={guide} />
          ))}
        </div>

        <div className="mt-6 text-center">
          <Link to="/marketplace">
            <Button variant="secondary" size="medium">
              Browse all skills
            </Button>
          </Link>
        </div>
      </section>

      {/* ── App Showcase ───────────────────────────────────────────────────── */}
      <section className="mx-auto mt-20 max-w-6xl">
        <div className="text-center">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
            The Experience
          </p>
          <h2 className="mt-3 font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl">
            Every screen earns your trust.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-kin-body/90">
            Quiet surfaces, semantic color, and dual typography create a health experience that
            feels calm and clinical in equal measure.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-start justify-center gap-7">
          {[
            { id: "home", screen: <TodayScreen />, active: "home" as const, label: "Home" },
            {
              id: "skills",
              screen: <SkillsScreen />,
              active: "skills" as const,
              label: "Skills",
            },
            {
              id: "insight",
              screen: <InsightScreen />,
              active: "skills" as const,
              label: "Insight",
              scrollable: true,
            },
          ].map((s) => (
            <div key={s.id} style={{ transform: "scale(0.82)", transformOrigin: "top center" }}>
              <Phone active={s.active} scrollable={s.scrollable}>
                {s.screen}
              </Phone>
              <p className="mt-4 text-center text-[10px] font-bold tracking-[0.18em] uppercase text-kin-muted">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How It Works ────────────────────────────────────────────────────── */}
      <section className="mx-auto mt-20 max-w-6xl">
        <div className="text-center">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
            How It Works
          </p>
          <h2 className="mt-3 font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl">
            From upload to insight in three steps.
          </h2>
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-3">
          {howSteps.map((step, index) => (
            <article
              key={step.num}
              className={`kin-surface rounded-3xl p-6 ${
                index === 0
                  ? "kin-surface-clouds"
                  : index === 1
                    ? "kin-surface-splash"
                    : "kin-surface-waves"
              }`}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full border border-kin-olive/14 bg-kin-white text-sm font-bold text-kin-black">
                {step.num}
              </div>
              <h3 className="mt-4 text-xl font-semibold text-kin-black">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-kin-body/90">{step.text}</p>
              <p className="mt-3 text-[11px] font-medium tracking-wide text-kin-muted">
                {step.detail}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* ── What You Get ────────────────────────────────────────────────────── */}
      <section className="mx-auto mt-20 max-w-6xl">
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Left: concrete output example */}
          <article className="kin-surface kin-surface-waves rounded-3xl p-6">
            <p className="text-xs font-semibold tracking-[0.16em] uppercase text-kin-muted">
              What a Skill Produces
            </p>
            <h3 className="mt-3 font-serif text-3xl font-bold leading-tight text-kin-black">
              Not just data. A clear plan.
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-kin-body/90">
              Every skill produces structured results you can review, track over time, and share
              with your doctor.
            </p>
            <div className="mt-5 space-y-2">
              {[
                ["Biomarker timeline", "18 months tracked", "default"],
                ["Protocol score", "72 / 100", "lavender"],
                ["Flagged markers", "3 need attention", "orange"],
                ["Doctor summary", "QR-ready to share", "gold"],
              ].map(([label, value, tone]) => (
                <div
                  key={label}
                  className="flex items-center justify-between rounded-xl border border-kin-olive/10 bg-kin-white/80 px-3.5 py-3"
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className={`h-2 w-2 rounded-full ${
                        tone === "lavender"
                          ? "bg-kin-lavender"
                          : tone === "orange"
                            ? "bg-kin-orange"
                            : tone === "gold"
                              ? "bg-kin-gold"
                              : "bg-kin-green"
                      }`}
                    />
                    <p className="text-sm text-kin-body">{label}</p>
                  </div>
                  <p className="text-sm font-semibold text-kin-black">{value}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-center">
              <div
                style={{
                  transform: "scale(0.52)",
                  transformOrigin: "top center",
                  // Phone is 570px tall; at 0.52 scale the rendered height is ~296px.
                  // Crop the bottom to show only the top ~76px (296 - 220).
                  marginBottom: -(570 * 0.52 - 76),
                }}
              >
                <Phone active="skills" scrollable>
                  <InsightScreen />
                </Phone>
              </div>
            </div>
          </article>

          {/* Right: vault value prop */}
          <article className="kin-surface kin-surface-clouds rounded-3xl p-6">
            <p className="text-xs font-semibold tracking-[0.16em] uppercase text-kin-muted">
              Your Vault
            </p>
            <h3 className="mt-3 font-serif text-3xl font-bold leading-tight text-kin-black">
              Every record in one place.
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-kin-body/90">
              Labs, wearable exports, clinical notes, genetics, CGM data — Kin structures and
              indexes everything so skills can analyze it.
            </p>

            <div className="mt-6 flex justify-center">
              <div
                style={{
                  transform: "scale(0.52)",
                  transformOrigin: "top center",
                  // Phone is 570px tall; at 0.52 scale the rendered height is ~296px.
                  // Crop the bottom to show only the top ~76px (296 - 220).
                  marginBottom: -(570 * 0.52 - 76),
                }}
              >
                <Phone active="profile">
                  <VaultScreen />
                </Phone>
              </div>
            </div>
            <div className="mt-5 space-y-2.5">
              {[
                {
                  label: "Lab Reports",
                  detail: "Blood panels, metabolic markers",
                  tone: "text-kin-gold-dark",
                },
                {
                  label: "Wearables",
                  detail: "Oura, Apple Health, Garmin",
                  tone: "text-kin-green-dark",
                },
                {
                  label: "CGM Data",
                  detail: "Continuous glucose monitoring",
                  tone: "text-kin-orange",
                },
                {
                  label: "Clinical Notes",
                  detail: "Doctor visits, imaging, Rx",
                  tone: "text-kin-muted",
                },
                {
                  label: "Genetics",
                  detail: "DNA markers, traits",
                  tone: "text-kin-lavender",
                },
              ].map((source) => (
                <div
                  key={source.label}
                  className="flex items-center gap-3 rounded-xl border border-kin-olive/10 bg-kin-white/80 px-3.5 py-2.5"
                >
                  <span
                    className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-kin-olive/10 bg-kin-white ${source.tone}`}
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden="true">
                      <path
                        d="M9 12l2 2 4-4"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.4" />
                    </svg>
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-kin-black">{source.label}</p>
                    <p className="text-xs text-kin-muted">{source.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>

      {/* ── Privacy & Trust ──────────────────────────────────────────────── */}
      <section className="mx-auto mt-20 max-w-6xl">
        <div className="text-center">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
            Privacy First
          </p>
          <h2 className="mt-3 font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl">
            Your data never leaves your control.
          </h2>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {privacyPoints.map((item) => (
            <article key={item.title} className="kin-surface rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-kin-olive/12 bg-kin-white text-kin-green-dark">
                  <PrivacyIcon icon={item.icon} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-kin-black">{item.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-kin-body/88">{item.text}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── For Creators teaser ──────────────────────────────────────────── */}
      <section className="mx-auto mt-20 max-w-6xl">
        <article className="kin-surface rounded-3xl p-6 sm:p-8">
          <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <Badge variant="lavender" className="normal-case tracking-normal">
                For health experts
              </Badge>
              <h2 className="mt-3 font-serif text-3xl font-bold leading-tight text-kin-black sm:text-4xl">
                Turn your methodology into an AI skill.
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-kin-body/90 sm:text-base">
                Package your health expertise as a skill on Kin. Reach thousands of users, earn
                revenue on every install, and let your protocols work at scale.
              </p>
            </div>
            <a href="mailto:hello@kinhealth.ai?subject=Become%20a%20Kin%20Creator">
              <Button variant="secondary" size="large">
                Become a Creator
              </Button>
            </a>
          </div>
        </article>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section id="join" className="mx-auto mt-20 max-w-6xl">
        <article className="kin-surface-strong relative overflow-hidden rounded-[34px] p-7 sm:p-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(135deg, rgba(227,218,255,0.62) 0%, rgba(255,255,255,0.86) 36%, rgba(255,226,138,0.48) 68%, rgba(255,138,77,0.28) 100%)",
            }}
          />
          <div className="relative text-center">
            <h2 className="mx-auto max-w-3xl font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl">
              Start understanding your health data today.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-kin-body/90 sm:text-lg">
              Create a vault, install your first skill, and see what your data has been trying to
              tell you.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/marketplace">
                <Button size="large">Browse Skills</Button>
              </Link>
              <a href={SIGNUP_FORM_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="secondary" size="large">
                  Create an Account
                </Button>
              </a>
            </div>
            <p className="mt-5 text-xs text-kin-muted">
              Kin supports clinician workflows but does not replace diagnosis or emergency care.
            </p>
          </div>
        </article>
      </section>
    </div>
  );
}
